import React, { useState, useMemo, useEffect } from 'react'
import {
  Button, Tabs, Table, Tag, Space, Modal, Form, Select, DatePicker,
  InputNumber, Input, Tooltip, theme, Typography, Row, Col,
  Checkbox, Popconfirm,
} from 'antd'
import {
  LeftOutlined, RightOutlined, PlusOutlined, EditOutlined,
  CarOutlined, CheckCircleOutlined, CloseCircleOutlined, PrinterOutlined,
} from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import type { ColumnsType } from 'antd/es/table'
import {
  useCourtesyCars,
  useCarAssignments,
  useCreateCarAssignment,
  useUpdateCarAssignment,
  useDeleteCarAssignment,
  useAvailableCourtesyCars,
} from '@/hooks/useCourtesyCars'
import { usePermission } from '@/hooks/usePermission'
import { useCustomers } from '@/hooks/useCustomers'
import { useWorkOrders } from '@/hooks/useWorkOrders'
import type { CarAssignmentFull, AssignmentStatus } from '@/types'
import { printAdcPlanning } from '@/components/courtesy-cars/AdcPrintView'

const { Text } = Typography
const { RangePicker } = DatePicker

const STATUS_COLOR: Record<string, string> = {
  prenotata: '#1677ff',
  in_corso: '#52c41a',
  completata: '#8c8c8c',
  annullata: '#ff4d4f',
}

const STATUS_LABEL: Record<string, string> = {
  prenotata: 'Prenotata',
  in_corso: 'In corso',
  completata: 'Completata',
  annullata: 'Annullata',
}

type ViewMode = 'gantt' | 'month' | 'table'

const CourtesyCarPlanningPanel: React.FC = () => {
  const { token } = theme.useToken()
  const canViewADC = usePermission('courtesy_cars.view')
  const canAssign = usePermission('courtesy_cars.assign')
  const canViewCustomers = usePermission('customers.view')
  const [weekStart, setWeekStart] = useState<Dayjs>(() => {
    const today = dayjs()
    const dow = today.day()
    const diffToMon = dow === 0 ? -6 : 1 - dow
    return today.add(diffToMon, 'day').startOf('day')
  })
  const [viewMode, setViewMode] = useState<ViewMode>('gantt')
  const [monthStart, setMonthStart] = useState<Dayjs>(() => dayjs().startOf('month'))

  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [editingAssignment, setEditingAssignment] = useState<CarAssignmentFull | null>(null)
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false)
  const [deliveryAssignment, setDeliveryAssignment] = useState<CarAssignmentFull | null>(null)
  const [returnModalOpen, setReturnModalOpen] = useState(false)
  const [returnAssignment, setReturnAssignment] = useState<CarAssignmentFull | null>(null)

  const weekEnd = weekStart.add(6, 'day').endOf('day')
  const monthEnd = monthStart.endOf('month')

  const fetchFrom = viewMode === 'month' ? monthStart.format('YYYY-MM-DD') : weekStart.format('YYYY-MM-DD')
  const fetchTo = viewMode === 'month' ? monthEnd.format('YYYY-MM-DD') : weekEnd.format('YYYY-MM-DD')
  const dateFrom = fetchFrom
  const dateTo = fetchTo

  const { data: assignments = [], isLoading: loadingAssignments } = useCarAssignments(dateFrom, dateTo, undefined, { enabled: canViewADC })
  const { data: allCarsData } = useCourtesyCars(1, 100, undefined, undefined, { enabled: canViewADC })
  const allCars = allCarsData?.items ?? []

  const createAssignment = useCreateCarAssignment()
  const updateAssignment = useUpdateCarAssignment()
  const deleteAssignment = useDeleteCarAssignment()

  const [assignForm] = Form.useForm()
  const [deliveryForm] = Form.useForm()
  const [returnForm] = Form.useForm()

  const [customerSearch, setCustomerSearch] = useState('')
  const { data: customersData } = useCustomers(1, 50, customerSearch, canViewCustomers)
  const customers = customersData?.items ?? []

  const selectedCustomerId = Form.useWatch('customer_id', assignForm) as number | undefined
  const { data: customerWoData } = useWorkOrders(
    1, 50, undefined, undefined, undefined, undefined, undefined, undefined, undefined,
    !editingAssignment ? selectedCustomerId : undefined
  )
  const customerWorkOrders = customerWoData?.items ?? []

  const assignRange = Form.useWatch('date_range', assignForm) as [Dayjs, Dayjs] | undefined
  const availableDateFrom = assignRange?.[0]?.format('YYYY-MM-DD')
  const availableDateTo = assignRange?.[1]?.format('YYYY-MM-DD')
  const { data: availableCars = [] } = useAvailableCourtesyCars(
    availableDateFrom,
    availableDateTo,
    editingAssignment?.id,
    { enabled: canViewADC }
  )

  useEffect(() => {
    if (editingAssignment || !selectedCustomerId || !assignModalOpen) return
    if (customerWorkOrders.length === 1) {
      assignForm.setFieldValue('work_order_id', customerWorkOrders[0].id)
    } else if (customerWorkOrders.length === 0) {
      assignForm.setFieldValue('work_order_id', undefined)
    }
  }, [customerWorkOrders, selectedCustomerId, editingAssignment, assignModalOpen])

  const prevWeek = () => setWeekStart(w => w.subtract(7, 'day'))
  const nextWeek = () => setWeekStart(w => w.add(7, 'day'))
  const prevMonth = () => setMonthStart(m => m.subtract(1, 'month'))
  const nextMonth = () => setMonthStart(m => m.add(1, 'month'))

  const days = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'))
  const monthDays = Array.from({ length: monthEnd.date() }, (_, i) => monthStart.add(i, 'day'))

  const goToday = () => {
    const today = dayjs()
    const dow = today.day()
    const diffToMon = dow === 0 ? -6 : 1 - dow
    setWeekStart(today.add(diffToMon, 'day').startOf('day'))
    setMonthStart(today.startOf('month'))
  }

  // ── Stampa pianificazione ADC ─────────────────────────────────────────────
  const handlePrintAdc = () => {
    const periodo = viewMode === 'month'
      ? monthStart.format('MMMM YYYY')
      : `settimana dal ${weekStart.format('D')} all'${weekEnd.format('D MMMM YYYY')}`
    const sorted = [...assignments].sort((a, b) => {
      const cmp = (a.courtesy_car_targa ?? '').localeCompare(b.courtesy_car_targa ?? '')
      return cmp !== 0 ? cmp : a.data_inizio.localeCompare(b.data_inizio)
    })
    printAdcPlanning(periodo, sorted)
  }

  const openNewAssignment = () => {
    setEditingAssignment(null)
    assignForm.resetFields()
    assignForm.setFieldsValue({ date_range: [weekStart, weekStart.add(1, 'day')] })
    setAssignModalOpen(true)
  }

  const openEditAssignment = (a: CarAssignmentFull) => {
    setEditingAssignment(a)
    assignForm.setFieldsValue({
      courtesy_car_id: a.courtesy_car_id,
      customer_id: a.customer_id,
      work_order_id: a.work_order_id ?? undefined,
      date_range: [dayjs(a.data_inizio), dayjs(a.data_fine_prevista)],
      km_inizio: a.km_inizio ?? undefined,
      note: a.note ?? undefined,
    })
    setAssignModalOpen(true)
  }

  const openDelivery = (a: CarAssignmentFull) => {
    setDeliveryAssignment(a)
    deliveryForm.setFieldsValue({ data_consegna: dayjs(), km_inizio: a.km_inizio ?? undefined })
    setDeliveryModalOpen(true)
  }

  const openReturn = (a: CarAssignmentFull) => {
    setReturnAssignment(a)
    returnForm.setFieldsValue({ data_restituzione: dayjs() })
    setReturnModalOpen(true)
  }

  const handleAssignSubmit = async () => {
    const vals = await assignForm.validateFields()
    const [start, end] = vals.date_range as [Dayjs, Dayjs]
    const payload = {
      courtesy_car_id: vals.courtesy_car_id as number,
      customer_id: vals.customer_id as number,
      work_order_id: (vals.work_order_id as number | undefined) || undefined,
      data_inizio: start.toISOString(),
      data_fine_prevista: end.toISOString(),
      km_inizio: (vals.km_inizio as number | undefined) || undefined,
      note: (vals.note as string | undefined) || undefined,
    }
    if (editingAssignment) {
      await updateAssignment.mutateAsync({
        id: editingAssignment.id,
        data: {
          courtesy_car_id: payload.courtesy_car_id,
          customer_id: payload.customer_id,
          data_inizio: payload.data_inizio,
          data_fine_prevista: payload.data_fine_prevista,
          km_inizio: payload.km_inizio,
          note: payload.note,
        },
      })
    } else {
      await createAssignment.mutateAsync(payload)
    }
    setAssignModalOpen(false)
    assignForm.resetFields()
  }

  const handleDeliverySubmit = async () => {
    if (!deliveryAssignment) return
    const vals = await deliveryForm.validateFields()
    await updateAssignment.mutateAsync({
      id: deliveryAssignment.id,
      data: {
        stato: 'IN_CORSO' as AssignmentStatus,
        km_inizio: vals.km_inizio as number,
        data_inizio: (vals.data_consegna as Dayjs).toISOString(),
      },
    })
    setDeliveryModalOpen(false)
  }

  const handleReturnSubmit = async () => {
    if (!returnAssignment) return
    const vals = await returnForm.validateFields()
    await updateAssignment.mutateAsync({
      id: returnAssignment.id,
      data: {
        stato: 'COMPLETATA' as AssignmentStatus,
        km_fine: vals.km_fine as number,
        data_fine_effettiva: (vals.data_restituzione as Dayjs).toISOString(),
        note: (vals.note as string | undefined) || undefined,
      },
    })
    setReturnModalOpen(false)
  }

  const handleAnnulla = async (a: CarAssignmentFull) => {
    await updateAssignment.mutateAsync({ id: a.id, data: { stato: 'ANNULLATA' as AssignmentStatus } })
  }

  const arrowClip = (left: boolean, right: boolean, px: number): string | undefined => {
    if (left && right) return `polygon(${px}px 0,calc(100% - ${px}px) 0,100% 50%,calc(100% - ${px}px) 100%,${px}px 100%,0 50%)`
    if (left)  return `polygon(${px}px 0,100% 0,100% 100%,${px}px 100%,0 50%)`
    if (right) return `polygon(0 0,calc(100% - ${px}px) 0,100% 50%,calc(100% - ${px}px) 100%,0 100%)`
    return undefined
  }

  const assignmentsByCar = useMemo(() => {
    const map: Record<number, CarAssignmentFull[]> = {}
    for (const a of assignments) {
      if (!map[a.courtesy_car_id]) map[a.courtesy_car_id] = []
      map[a.courtesy_car_id].push(a)
    }
    return map
  }, [assignments])

  const renderGantt = () => {
    const B = `1px solid ${token.colorBorder}`
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 12 }}>
          <colgroup>
            <col style={{ width: 130 }} />
            {days.map(d => <col key={d.format('YYYY-MM-DD')} />)}
          </colgroup>
          <thead>
            <tr>
              <th style={{ border: B, padding: '5px 8px', textAlign: 'left', background: token.colorFillAlter, fontWeight: 600, fontSize: 11, color: token.colorTextSecondary }}>
                Auto
              </th>
              {days.map(d => {
                const isToday = dayjs().isSame(d, 'day')
                return (
                  <th key={d.format('YYYY-MM-DD')} style={{ border: B, padding: '5px 4px', textAlign: 'center', background: isToday ? token.colorPrimaryBg : token.colorFillAlter, color: isToday ? token.colorPrimary : token.colorTextSecondary, fontWeight: isToday ? 700 : 500, fontSize: 11 }}>
                    <div>{d.format('ddd')}</div>
                    <div style={{ fontSize: 10 }}>{d.format('D/M')}</div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {allCars.length === 0 && (
              <tr><td colSpan={8} style={{ border: B, textAlign: 'center', padding: 20, color: token.colorTextSecondary }}>Nessuna auto di cortesia configurata</td></tr>
            )}
            {allCars.map(car => {
              const carAssignments = (assignmentsByCar[car.id] ?? []).filter(a => a.stato !== 'ANNULLATA')
              return (
                <tr key={car.id}>
                  <td style={{ border: B, padding: '4px 8px', background: token.colorBgContainer, verticalAlign: 'middle', height: 38 }}>
                    <div style={{ fontWeight: 700, fontSize: 12, lineHeight: 1.3 }}>{(car as any).vehicle?.targa ?? `#${car.id}`}</div>
                    <div style={{ fontSize: 10, color: token.colorTextSecondary, lineHeight: 1.3 }}>{[(car as any).vehicle?.marca, (car as any).vehicle?.modello].filter(Boolean).join(' ') || ''}</div>
                  </td>
                  {days.map(day => {
                    const dayStart = day.startOf('day').valueOf()
                    const dayEnd = day.endOf('day').valueOf()
                    const assignment = carAssignments.find(a => {
                      const aS = dayjs(a.data_inizio).valueOf()
                      const aE = dayjs(a.data_fine_prevista).valueOf()
                      return aS <= dayEnd && aE >= dayStart
                    })
                    const isToday = dayjs().isSame(day, 'day')
                    const color = assignment ? STATUS_COLOR[assignment.stato] : undefined
                    const extendsLeft = !!assignment && dayjs(assignment.data_inizio).isBefore(weekStart, 'day') && day.isSame(weekStart, 'day')
                    const extendsRight = !!assignment && dayjs(assignment.data_fine_prevista).isAfter(weekEnd, 'day') && day.isSame(days[6], 'day')
                    const clip = arrowClip(extendsLeft, extendsRight, 8)
                    return (
                      <td key={day.format('YYYY-MM-DD')} onClick={assignment && canAssign ? () => openEditAssignment(assignment) : undefined}
                        style={{ border: B, borderLeft: color ? `3px solid ${color}` : B, padding: '3px 5px', height: 38, position: 'relative', background: isToday ? token.colorPrimaryBg : token.colorBgContainer, cursor: assignment && canAssign ? 'pointer' : 'default', verticalAlign: 'middle', overflow: 'hidden' }}>
                        {color && <div style={{ position: 'absolute', inset: 0, background: `${color}45`, clipPath: clip }} />}
                        {assignment && (
                          <Tooltip title={<div><div><b>{assignment.customer_nome ?? `Cliente #${assignment.customer_id}`}</b></div>{assignment.work_order_codice && <div>OdL: {assignment.work_order_codice}</div>}<div>{dayjs(assignment.data_inizio).format('DD/MM HH:mm')} → {dayjs(assignment.data_fine_prevista).format('DD/MM HH:mm')}</div><div>{STATUS_LABEL[assignment.stato]}</div></div>}>
                            <div style={{ position: 'relative', zIndex: 1, lineHeight: 1.3 }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{STATUS_LABEL[assignment.stato]}</div>
                              <div style={{ fontSize: 10, color: token.colorText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{assignment.customer_nome ?? ''}</div>
                            </div>
                          </Tooltip>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
        <Row gutter={16} style={{ marginTop: 10 }}>
          {Object.entries(STATUS_LABEL).map(([k, label]) => (
            <Col key={k}><Space size={4}><div style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLOR[k], display: 'inline-block' }} /><Text style={{ fontSize: 11 }}>{label}</Text></Space></Col>
          ))}
        </Row>
      </div>
    )
  }

  const renderMonth = () => {
    const B = `1px solid ${token.colorBorder}`
    const DOW = ['L', 'M', 'M', 'G', 'V', 'S', 'D']
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: monthDays.length * 32 + 130, borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 11 }}>
          <colgroup>
            <col style={{ width: 130 }} />
            {monthDays.map(d => <col key={d.format('YYYY-MM-DD')} style={{ minWidth: 32 }} />)}
          </colgroup>
          <thead>
            <tr>
              <th style={{ border: B, padding: '5px 8px', textAlign: 'left', background: token.colorFillAlter, fontWeight: 600, fontSize: 11, color: token.colorTextSecondary }}>Auto</th>
              {monthDays.map(d => {
                const isToday = dayjs().isSame(d, 'day')
                const dow = d.day()
                const isWeekend = dow === 0 || dow === 6
                return (
                  <th key={d.format('YYYY-MM-DD')} style={{ border: B, padding: '3px 2px', textAlign: 'center', background: isToday ? token.colorPrimaryBg : isWeekend ? token.colorFillSecondary : token.colorFillAlter, color: isToday ? token.colorPrimary : token.colorTextSecondary, fontWeight: isToday ? 700 : 400, fontSize: 10 }}>
                    <div>{DOW[(dow + 6) % 7]}</div>
                    <div style={{ fontWeight: 600 }}>{d.format('D')}</div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {allCars.length === 0 && (
              <tr><td colSpan={monthDays.length + 1} style={{ border: B, textAlign: 'center', padding: 20, color: token.colorTextSecondary }}>Nessuna auto di cortesia configurata</td></tr>
            )}
            {allCars.map(car => {
              const carAssignments = (assignmentsByCar[car.id] ?? []).filter(a => a.stato !== 'ANNULLATA')
              return (
                <tr key={car.id}>
                  <td style={{ border: B, padding: '4px 8px', background: token.colorBgContainer, verticalAlign: 'middle', height: 36 }}>
                    <div style={{ fontWeight: 700, fontSize: 12, lineHeight: 1.3 }}>{(car as any).vehicle?.targa ?? `#${car.id}`}</div>
                    <div style={{ fontSize: 10, color: token.colorTextSecondary, lineHeight: 1.3 }}>{[(car as any).vehicle?.marca, (car as any).vehicle?.modello].filter(Boolean).join(' ') || ''}</div>
                  </td>
                  {monthDays.map(day => {
                    const dayStart = day.startOf('day').valueOf()
                    const dayEnd = day.endOf('day').valueOf()
                    const assignment = carAssignments.find(a => {
                      const aS = dayjs(a.data_inizio).valueOf()
                      const aE = dayjs(a.data_fine_prevista).valueOf()
                      return aS <= dayEnd && aE >= dayStart
                    })
                    const isToday = dayjs().isSame(day, 'day')
                    const dow = day.day()
                    const isWeekend = dow === 0 || dow === 6
                    const color = assignment ? STATUS_COLOR[assignment.stato] : undefined
                    const extendsLeft = !!assignment && dayjs(assignment.data_inizio).isBefore(monthStart, 'day') && day.isSame(monthStart, 'day')
                    const extendsRight = !!assignment && dayjs(assignment.data_fine_prevista).isAfter(monthEnd, 'day') && day.isSame(monthDays[monthDays.length - 1], 'day')
                    const clip = arrowClip(extendsLeft, extendsRight, 6)
                    return (
                      <td key={day.format('YYYY-MM-DD')} onClick={assignment && canAssign ? () => openEditAssignment(assignment) : undefined}
                        style={{ border: B, borderLeft: color ? `3px solid ${color}` : B, height: 36, padding: 0, position: 'relative', background: isToday ? token.colorPrimaryBg : isWeekend ? token.colorFillSecondary : token.colorBgContainer, cursor: assignment && canAssign ? 'pointer' : 'default', verticalAlign: 'middle', textAlign: 'center', overflow: 'hidden' }}>
                        {color && <div style={{ position: 'absolute', inset: 0, background: `${color}45`, clipPath: clip }} />}
                        {assignment && (
                          <Tooltip title={<div><div><b>{assignment.customer_nome ?? `Cliente #${assignment.customer_id}`}</b></div>{assignment.work_order_codice && <div>OdL: {assignment.work_order_codice}</div>}<div>{dayjs(assignment.data_inizio).format('DD/MM')} → {dayjs(assignment.data_fine_prevista).format('DD/MM')}</div><div>{STATUS_LABEL[assignment.stato]}</div></div>}>
                            <div style={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
                            </div>
                          </Tooltip>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
        <Row gutter={16} style={{ marginTop: 10 }}>
          {Object.entries(STATUS_LABEL).map(([k, label]) => (
            <Col key={k}><Space size={4}><div style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLOR[k], display: 'inline-block' }} /><Text style={{ fontSize: 11 }}>{label}</Text></Space></Col>
          ))}
        </Row>
      </div>
    )
  }

  const tableColumns: ColumnsType<CarAssignmentFull> = [
    {
      title: 'Auto', key: 'auto',
      render: (_, r) => (<><b>{r.courtesy_car_targa ?? `#${r.courtesy_car_id}`}</b>{r.courtesy_car_modello && (<><br /><Text type="secondary" style={{ fontSize: 11 }}>{r.courtesy_car_modello}</Text></>)}</>),
    },
    { title: 'Cliente', dataIndex: 'customer_nome', render: v => v ?? '-' },
    { title: 'OdL', dataIndex: 'work_order_codice', render: v => v ?? '-' },
    { title: 'Dal', dataIndex: 'data_inizio', render: v => dayjs(v).format('DD/MM/YY HH:mm') },
    { title: 'Al previsto', dataIndex: 'data_fine_prevista', render: v => dayjs(v).format('DD/MM/YY HH:mm') },
    { title: 'Al effettivo', dataIndex: 'data_fine_effettiva', render: v => v ? dayjs(v).format('DD/MM/YY HH:mm') : '-' },
    { title: 'Km', key: 'km', render: (_, r) => r.km_inizio != null ? `${r.km_inizio}${r.km_fine != null ? ` → ${r.km_fine}` : ''}` : '-' },
    { title: 'Stato', dataIndex: 'stato', render: v => <Tag color={STATUS_COLOR[v] ?? 'default'}>{STATUS_LABEL[v] ?? v}</Tag> },
    {
      title: 'Azioni', key: 'azioni',
      render: (_, record) => (
        <Space size="small" wrap>
          {canAssign && <Button size="small" icon={<EditOutlined />} onClick={() => openEditAssignment(record)}>Modifica</Button>}
          {record.stato === 'PRENOTATA' && <Button size="small" type="primary" icon={<CarOutlined />} onClick={() => openDelivery(record)}>Consegna</Button>}
          {record.stato === 'IN_CORSO' && <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => openReturn(record)}>Restituzione</Button>}
          {canAssign && (record.stato === 'PRENOTATA' || record.stato === 'IN_CORSO') && (
            <Popconfirm title="Annullare questa assegnazione?" onConfirm={() => handleAnnulla(record)}>
              <Button size="small" danger icon={<CloseCircleOutlined />}>Annulla</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: '12px 20px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space>
            <Button icon={<LeftOutlined />} onClick={viewMode === 'month' ? prevMonth : prevWeek} size="small" />
            <Text strong style={{ minWidth: 200, textAlign: 'center', display: 'inline-block', fontSize: 13 }}>
              {viewMode === 'month'
                ? monthStart.format('MMMM YYYY')
                : `${weekStart.format('D MMM')} – ${weekEnd.format('D MMM YYYY')}`}
            </Text>
            <Button icon={<RightOutlined />} onClick={viewMode === 'month' ? nextMonth : nextWeek} size="small" />
            <Button onClick={goToday} size="small">Oggi</Button>
            <Button size="small" icon={<PrinterOutlined />} onClick={handlePrintAdc}>Stampa</Button>
          </Space>
        </Col>
        {canAssign && (
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={openNewAssignment} size="small">
              Nuova assegnazione
            </Button>
          </Col>
        )}
      </Row>

      <Tabs
        activeKey={viewMode}
        onChange={k => setViewMode(k as ViewMode)}
        size="small"
        items={[
          { key: 'gantt', label: 'Settimana', children: renderGantt() },
          { key: 'month', label: 'Mese', children: renderMonth() },
          {
            key: 'table', label: 'Tabella',
            children: (
              <Table<CarAssignmentFull>
                dataSource={assignments}
                columns={tableColumns}
                rowKey="id"
                loading={loadingAssignments}
                size="small"
                pagination={{ pageSize: 50 }}
              />
            ),
          },
        ]}
      />

      {/* Modal: Nuova/Modifica assegnazione */}
      <Modal
        title={editingAssignment ? 'Modifica assegnazione' : 'Nuova assegnazione'}
        open={assignModalOpen}
        onCancel={() => { setAssignModalOpen(false); assignForm.resetFields() }}
        onOk={handleAssignSubmit}
        confirmLoading={createAssignment.isPending || updateAssignment.isPending}
        okText={editingAssignment ? 'Salva' : 'Crea'}
        destroyOnClose
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            {editingAssignment && (
              <Popconfirm
                title="Eliminare questa assegnazione?"
                onConfirm={async () => {
                  await deleteAssignment.mutateAsync(editingAssignment.id)
                  setAssignModalOpen(false)
                  assignForm.resetFields()
                  setEditingAssignment(null)
                }}
                okText="Elimina" cancelText="Annulla" okButtonProps={{ danger: true }}
              >
                <Button danger loading={deleteAssignment.isPending} style={{ marginRight: 'auto' }}>Elimina</Button>
              </Popconfirm>
            )}
            <CancelBtn />
            <OkBtn />
          </>
        )}
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item name="date_range" label="Periodo" rules={[{ required: true, message: 'Seleziona periodo' }]}>
            <RangePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="courtesy_car_id" label="Auto di cortesia" rules={[{ required: true, message: 'Seleziona auto' }]}>
            <Select
              showSearch
              placeholder={editingAssignment ? 'Auto non modificabile' : availableDateFrom ? 'Auto disponibili nel periodo' : 'Seleziona prima il periodo'}
              filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
              options={(editingAssignment ? allCars : availableCars).map(c => ({
                value: c.id,
                label: `${(c as any).vehicle?.targa ?? `#${c.id}`}${[(c as any).vehicle?.marca, (c as any).vehicle?.modello].filter(Boolean).length ? ` – ${[(c as any).vehicle?.marca, (c as any).vehicle?.modello].filter(Boolean).join(' ')}` : ''}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="customer_id" label="Cliente" rules={[{ required: true, message: 'Seleziona cliente' }]}>
            <Select showSearch placeholder="Cerca cliente..." filterOption={false} onSearch={v => setCustomerSearch(v)}
              options={customers.map(c => ({ value: c.id, label: [c.nome, c.cognome].filter(Boolean).join(' ') || c.ragione_sociale || `#${c.id}` }))} />
          </Form.Item>
          <Form.Item name="work_order_id" label={selectedCustomerId && customerWorkOrders.length > 0 ? `Scheda lavoro (${customerWorkOrders.length} aperta${customerWorkOrders.length > 1 ? 'e' : ''})` : 'Scheda lavoro (opzionale)'}>
            <Select showSearch allowClear
              placeholder={!selectedCustomerId ? 'Seleziona prima il cliente' : customerWorkOrders.length === 0 ? 'Nessuna scheda aperta per questo cliente' : 'Seleziona scheda lavoro...'}
              filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
              options={customerWorkOrders.map(w => ({ value: w.id, label: `${(w as any).numero_scheda ?? `#${w.id}`}` }))}
              disabled={!!editingAssignment || !selectedCustomerId} />
          </Form.Item>
          <Form.Item name="km_inizio" label="Km iniziali">
            <InputNumber style={{ width: '100%' }} min={0} placeholder="Es. 45000" />
          </Form.Item>
          <Form.Item name="note" label="Note">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal: Conferma consegna */}
      <Modal title="Conferma consegna auto" open={deliveryModalOpen} onCancel={() => setDeliveryModalOpen(false)} onOk={handleDeliverySubmit} confirmLoading={updateAssignment.isPending} okText="Conferma consegna" destroyOnClose>
        <Form form={deliveryForm} layout="vertical">
          <Form.Item name="data_consegna" label="Data/ora consegna effettiva" rules={[{ required: true, message: 'Inserisci data' }]}>
            <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="km_inizio" label="Km al momento della consegna" rules={[{ required: true, message: 'Inserisci km' }]}>
            <InputNumber style={{ width: '100%' }} min={0} placeholder="Es. 45000" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal: Registra restituzione */}
      <Modal title="Registra restituzione" open={returnModalOpen} onCancel={() => setReturnModalOpen(false)} onOk={handleReturnSubmit} confirmLoading={updateAssignment.isPending} okText="Registra restituzione" destroyOnClose>
        <Form form={returnForm} layout="vertical">
          <Form.Item name="data_restituzione" label="Data/ora restituzione" rules={[{ required: true, message: 'Inserisci data' }]}>
            <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="km_fine" label="Km finali" rules={[{ required: true, message: 'Inserisci km' }]}>
            <InputNumber style={{ width: '100%' }} min={0} placeholder="Es. 47500" />
          </Form.Item>
          <Form.Item name="note" label="Note condizioni">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="needs_maintenance" valuePropName="checked">
            <Checkbox>Necessita manutenzione</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default CourtesyCarPlanningPanel
